import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { OrderStatus } from '@lds-efsp/shared-contracts';
import { ORDER_STATUS_CONFIG } from './status-config';

interface StatusConfig {
  bg: string;
  text: string;
}

interface StatusBadgeProps {
  status: string;
  brand: BrandConfig;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, brand }) => {
  const statusConfig: Record<string, StatusConfig> = {
    'OVERDUE': { bg: brand.colors.error, text: 'white' },
    'URGENT': { bg: brand.colors.warning, text: 'white' },
    'ROUTINE': { bg: brand.colors.info, text: 'white' },
    'Received': { bg: '#D1FAE5', text: '#065F46' },
    'Waiting': { bg: '#FEF3C7', text: '#92400E' },
    'Dispatched': { bg: '#DBEAFE', text: '#1E40AF' },
    'On Hold': { bg: '#F3F4F6', text: '#4B5563' },
    'Ready to Post': { bg: '#D1FAE5', text: '#065F46' },
    'Payment Error': { bg: '#FEE2E2', text: '#991B1B' },
    'Post Error': { bg: '#FFEDD5', text: '#9A3412' },
    'In Progress': { bg: '#DBEAFE', text: '#1E40AF' },
    'Pending Review': { bg: '#FEF3C7', text: '#92400E' },
    'Accepted': { bg: '#D1FAE5', text: '#065F46' },
    'Completed': { bg: '#E0E7FF', text: '#3730A3' }
  };

  // Resolve a kebab-case OrderStatus first (human-readable label), then fall back to
  // the existing title-case admin/invoice config, then the gray default. A plain index
  // lookup returns undefined for any non-OrderStatus string, so no runtime parse needed.
  const orderEntry = ORDER_STATUS_CONFIG[status as OrderStatus];
  const config = orderEntry ?? statusConfig[status] ?? { bg: '#F3F4F6', text: '#4B5563' };
  const label = orderEntry?.label ?? status;

  return (
    <span
      className="inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full"
      style={{
        backgroundColor: config.bg,
        color: config.text,
      }}
    >
      {label}
    </span>
  );
};

interface DueDateDisplayProps {
  dueDate: string | Date;
  isOverdue: boolean;
  daysUntilDue: number;
  brand: BrandConfig;
}

export const DueDateDisplay: React.FC<DueDateDisplayProps> = ({ dueDate, isOverdue, daysUntilDue, brand }) => {
  const formatDate = (date: string | Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (date: string | Date): string => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="flex flex-col">
      <div className="text-sm" style={{ color: brand.colors.text }}>
        {formatDate(dueDate)} by {formatTime(dueDate)}
      </div>
      <div
        className="text-xs font-medium"
        style={{ color: isOverdue ? brand.colors.error : brand.colors.textLight }}
      >
        {isOverdue ? 'OVERDUE' : daysUntilDue === 0 ? 'Due today' : `in ${daysUntilDue} days`}
      </div>
    </div>
  );
};

interface OrderTypeBadgeProps {
  type: string;
  subType?: string;
  brand: BrandConfig;
}

export const OrderTypeBadge: React.FC<OrderTypeBadgeProps> = ({ type, subType, brand }) => {
  return (
    <div className="flex flex-col">
      <span className="text-sm font-medium" style={{ color: brand.colors.text }}>{type}</span>
      {subType && <span className="text-xs" style={{ color: brand.colors.textLight }}>{subType}</span>}
    </div>
  );
};

type OrderSource = 'portal' | 'concierge' | 'api';

interface SourceBadgeProps {
  source: OrderSource;
}

const SOURCE_STYLES: Record<OrderSource, { bg: string; text: string; label: string }> = {
  portal: { bg: '#DBEAFE', text: '#1E40AF', label: 'Portal' },
  concierge: { bg: '#EDE9FE', text: '#5B21B6', label: 'Concierge' },
  api: { bg: '#D1FAE5', text: '#065F46', label: 'API' },
};

export const SourceBadge: React.FC<SourceBadgeProps> = ({ source }) => {
  const style = SOURCE_STYLES[source] || SOURCE_STYLES.portal;
  return (
    <span
      className="inline-flex px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {style.label}
    </span>
  );
};
